/*

  UserInfoDialog.ts

  User information dialog component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, RecentListItem, UserStats } from "./types";
import { Component, m } from "./mithril";
import { glyph, glyphGrayed, nbsp } from "./util";
import { mt, t, ts } from "./i18n";
import { RecentList } from "./recentlist";
import { StatsDisplay } from "./statsdisplay";
import { BestDisplay } from "./bestdisplay";
import { DialogButton, ProModeIndicator } from "./components";

interface IAttributes {
  view: IView;
  userid: string;
  nick: string;
  fullname: string;
}

interface IState {
  loadingStats: boolean;
  loadingRecentList: boolean;
  stats: UserStats;
  recentList: RecentListItem[];
  versusAll: boolean;
}

const _updateStats = (attrs: IAttributes, state: IState) => {
  // Fetch the statistics of the given user
  if (state.loadingStats)
    return;
  state.loadingStats = true;
  const { model } = attrs.view;
  model.loadUserStats(attrs.userid,
    (json: UserStats | { result: number; }) => {
      if (json && json.result === 0)
        state.stats = json;
      else
        state.stats = {};
      state.loadingStats = false;
      // m.redraw();
    }
  );
};

const _updateRecentList = (attrs: IAttributes, state: IState) => {
  // Fetch the recent game list of the given user
  if (state.loadingRecentList)
    return;
  state.loadingRecentList = true;
  const { model } = attrs.view;
  model.loadUserRecentList(attrs.userid,
    state.versusAll ? null : model.state?.netskraflUserId ?? "",
    (json: { result: number; recentlist: RecentListItem[]; }) => {
      if (json && json.result === 0)
        state.recentList = json.recentlist;
      else
        state.recentList = [];
      state.loadingRecentList = false;
      // m.redraw();
    }
  );
};

const _setVersus = (attrs: IAttributes, state: IState, vsState: boolean) => {
  if (state.versusAll != vsState) {
    state.versusAll = vsState;
    state.loadingRecentList = false;
    _updateRecentList(attrs, state);
  }
};

export const UserInfoDialog: Component<IAttributes, IState> = {

  // A dialog showing the track record of a given user, including
  // recent games and total statistics

  /*
  const view = initialVnode.attrs.view;
  const model = view.model;
  let stats: UserStats = {};
  let recentList: RecentListItem[] = [];
  let versusAll = true; // Show games against all opponents or just the current user?
  let loadingStats = false;
  let loadingRecentList = false;
  */

  oninit: (vnode) => {
    const { attrs, state } = vnode;
    state.stats = {};
    state.recentList = [];
    state.versusAll = true;
    state.loadingRecentList = false;
    state.loadingStats = false;
    _updateRecentList(attrs, state);
    _updateStats(attrs, state);
  },

  view: (vnode) => {
    const { attrs, state } = vnode;
    const { view } = attrs;
    const { stats, recentList, versusAll } = state;
    return m(".modal-dialog",
      { id: 'usr-info-dialog', style: { visibility: "visible" } },
      m(".ui-widget.ui-widget-content.ui-corner-all", { id: 'usr-info-form' },
        [
          m(".usr-info-hdr",
            [
              m("h1.usr-info-icon",
                [
                  vnode.state.stats.friend ?
                    glyph("coffee-cup", { title: ts('Áskrifandi') }) :
                    glyph("user"), nbsp()
                ]
              ),
              m("h1[id='usr-info-nick']", vnode.attrs.nick),
              m("span.vbar", "|"),
              m("h2[id='usr-info-fullname']", vnode.attrs.fullname),
              m(".usr-info-fav",
                {
                  title: ts('Uppáhald'),
                  onclick: (ev: Event) => {
                    // Toggle the favorite setting
                    ev.preventDefault();
                    view.actions.toggleFavorite(vnode.attrs.userid, stats.favorite ?? false);
                    stats.favorite = !stats.favorite;
                  }
                },
                stats.favorite ? glyph("star") : glyph("star-empty")
              )
            ]
          ),
          m("p",
            [
              m("strong", t("Nýjustu viðureignir")),
              nbsp(),
              m("span.versus-cat",
                [
                  m("span",
                    {
                      class: versusAll ? "shown" : "",
                      onclick: () => { _setVersus(attrs, state, true); } // Set this.versusAll to true
                    },
                    t(" gegn öllum ")
                  ),
                  m("span",
                    {
                      class: versusAll ? "" : "shown",
                      onclick: () => { _setVersus(attrs, state, false); } // Set this.versusAll to false
                    },
                    t(" gegn þér ")
                  )
                ]
              )
            ]
          ),
          m(".listitem.listheader",
            [
              m("span.list-win", glyphGrayed("bookmark", { title: ts('Sigur') })),
              mt("span.list-ts-short", "Viðureign lauk"),
              mt("span.list-nick", "Andstæðingur"),
              mt("span.list-scorehdr", "Úrslit"),
              m("span.list-elo-hdr",
                [
                  m("span.glyphicon.glyphicon-user.elo-hdr-left", { title: ts('Mennskir andstæðingar') }),
                  "Elo",
                  m("span.glyphicon.glyphicon-cog.elo-hdr-right", { title: ts('Allir andstæðingar') })
                ]
              ),
              mt("span.list-duration", "Lengd"),
              m(ProModeIndicator, { header: true })
            ]
          ),
          m(RecentList, { view, id: 'usr-recent', recentList }), // Recent game list
          m(StatsDisplay, { view, id: 'usr-stats', ownStats: stats }),
          m(BestDisplay, { id: 'usr-best', ownStats: stats, myself: false }), // Highest word and game scores
          m(DialogButton,
            {
              id: 'usr-info-close',
              title: ts('Loka'),
              onclick: (ev: Event) => { view.popDialog(); ev.preventDefault(); }
            },
            glyph("ok")
          )
        ]
      )
    );
  },

};
